/**
 * Header Search Bar — shows compact search in the fixed header when scrolled far enough.
 *
 * Sets two CSS variables:
 *   --header-h    = header height only (stable, used for #main-content padding-top)
 *   --header-full = header + header-search when visible (used for sticky toolbar, filters modal)
 */

export const StickySearch = {
	init() {
		// Search-results page has its own --header-h logic (forceHeaderSearchVisible).
		if ( document.querySelector( '[data-search-results-page]' ) ) return;

		// Header-search is hidden via CSS on residence & informational pages.
		const body = document.body;
		this.hideSearch = body.classList.contains( 'single-residence' )
			|| body.classList.contains( 'single-short_stay' )
			|| ( body.classList.contains( 'page' )
				&& ! body.classList.contains( 'home' )
				&& ! body.classList.contains( 'page-search-results' ) );

		this.hero         = document.querySelector( '.block-hero' );
		this.heroSearch   = document.querySelector( '[data-hero-search]' );
		this.headerSearch = document.querySelector( '[data-header-search]' );
		this.header       = document.querySelector( '.site-header' );

		if ( ! this.header ) return;

		this.isVisible = false;

		// Always set --header-h / --header-full for fixed header offset.
		this.updateHeaderHeight();
		window.addEventListener( 'resize', () => this.updateHeaderHeight() );
		window.addEventListener( 'load', () => this.updateHeaderHeight() );

		// ResizeObserver catches header height changes (e.g. compact mode).
		if ( window.ResizeObserver ) {
			new ResizeObserver( () => this.updateHeaderHeight() ).observe( this.header );
		}

		if ( ! this.headerSearch || this.hideSearch ) return;

		// Pages without a hero search bar (e.g. location pages without hero):
		// show header search after scrolling 300px.
		if ( ! this.hero || ! this.heroSearch ) {
			this.ticking   = false;
			this.threshold = 300;
			this.onScroll();
			window.addEventListener( 'scroll', () => this.requestTick(), { passive: true } );
			return;
		}

		this.ticking = false;

		this.calculateThreshold();
		this.onScroll();

		window.addEventListener( 'scroll', () => this.requestTick(), { passive: true } );
		window.addEventListener( 'resize', () => {
			this.calculateThreshold();
			this.onScroll();
		} );
	},

	updateHeaderHeight() {
		// getBoundingClientRect().bottom is relative to the viewport. When
		// wp-admin-bar is visible it pins itself at top:0 and pushes html
		// down by its own height (32px desktop / 46px ≤ 782px), so the
		// header's viewport-bottom already includes that offset. Strip it
		// out so --header-h reflects just the header itself — otherwise
		// #main-content gets padding-top equal to header+admin-bar and
		// leaves a visible gap below the header.
		const rect    = this.header.getBoundingClientRect();
		let headerBot = Math.round( rect.bottom );

		const adminBar = document.getElementById( 'wpadminbar' );
		if ( adminBar && adminBar.offsetHeight ) {
			headerBot = Math.max( 0, headerBot - adminBar.offsetHeight );
		}

		// --header-h: stable value for padding-top (never includes header-search).
		document.documentElement.style.setProperty( '--header-h', headerBot + 'px' );

		// --header-full: includes header-search when visible (for sticky elements & modals).
		const searchH = ( this.headerSearch && this.isVisible )
			? this.headerSearch.offsetHeight
			: 0;
		document.documentElement.style.setProperty( '--header-full', ( headerBot + searchH ) + 'px' );
	},

	calculateThreshold() {
		// Primary: show header search when hero search bar scrolls out of view.
		const rect = this.heroSearch.getBoundingClientRect();
		const heroSearchBottom = window.scrollY + rect.top + rect.height;
		this.threshold = Math.max( heroSearchBottom - this.header.offsetHeight, 100 );
	},

	requestTick() {
		if ( ! this.ticking ) {
			this.ticking = true;
			requestAnimationFrame( () => {
				this.onScroll();
				this.ticking = false;
			} );
		}
	},

	onScroll() {
		const buffer = 50;
		const scrollY = window.scrollY;

		if ( ! this.isVisible && scrollY >= this.threshold + buffer ) {
			this.showHeaderSearch();
		} else if ( this.isVisible && scrollY < this.threshold - buffer ) {
			this.hideHeaderSearch();
		}
	},

	showHeaderSearch() {
		this.isVisible = true;
		this.headerSearch.classList.add( 'is-visible' );
		this.headerSearch.setAttribute( 'aria-hidden', 'false' );
		this.updateHeaderHeight();
	},

	hideHeaderSearch() {
		this.isVisible = false;
		this.headerSearch.classList.remove( 'is-visible' );
		this.headerSearch.setAttribute( 'aria-hidden', 'true' );
		this.updateHeaderHeight();
	},
};
